import React, { useEffect, useState } from 'react';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { QuoteReferenceDataForm } from '../../../../app/models/Quote';
import { PaymentMethod } from '../../../../app/models/PaymentMethod';
import { Currency } from '../../../../app/models/Currency';
import { ExchangeRateDate } from '../../../../app/models/ExchangeRateDate';
import { ExchangeRateDateSource } from '../../../../app/models/ExchangeRateDateSource';
import { QuoteInvoicingDataForm, quoteMaxPaymentsOptions } from '../../../../app/models/QuoteInvoicing';
import { CLPcurrency, momentParseDate } from '../../../../helpers';
import { ExchangeRateDateEnum, QuoteMaxPaymentsEnum } from '../../../../app/shared/enums';
import { Unit } from '../../../../app/models/Unit';

interface Props {
  quoteInvoicingDataForm: QuoteInvoicingDataForm;
  setQuoteInvoicingDataForm: (quote: QuoteInvoicingDataForm) => void;
  paymentMethods: PaymentMethod[];
  currencies: Currency[];
  references: QuoteReferenceDataForm[];
  exchangeRateDate: ExchangeRateDate[];
  exchangeRateSource: ExchangeRateDateSource[];
  units: Unit[];
  errorFields?: Record<string, unknown>;
}

const QuoteInvoicingForm: React.FC<Props> = ({
  quoteInvoicingDataForm,
  setQuoteInvoicingDataForm,
  paymentMethods,
  currencies,
  references,
  exchangeRateDate,
  exchangeRateSource,
  units,
  errorFields
}) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
  const [showPrelimFixed, setShowPrelimFixed] = useState(false);
  const [showFinalFixed, setShowFinalFixed] = useState(false);
  const [isCLP, setIsCLP] = useState(false);

  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields]);

    useEffect(() => {
      setIsCLP(CLPcurrency(quoteInvoicingDataForm.currency_id, currencies))

  }, [quoteInvoicingDataForm]);

  useEffect(() => {
    setShowPrelimFixed(
      quoteInvoicingDataForm.preliminary_exchange_rate_date_reference === ExchangeRateDateEnum.FECHA_FIJA
    );
  }, [quoteInvoicingDataForm.preliminary_exchange_rate_date_reference]);

  useEffect(() => {
    setShowFinalFixed(
      quoteInvoicingDataForm.final_exchange_rate_date_reference === ExchangeRateDateEnum.FECHA_FIJA
    );
  }, [quoteInvoicingDataForm.final_exchange_rate_date_reference]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement| HTMLSelectElement>) => {
    const { name, value } = e.target;
    setQuoteInvoicingDataForm({ ...quoteInvoicingDataForm, [name]: value });
  };

  const handleSelect = (name: string, value: number | string | null) => {
    setQuoteInvoicingDataForm({ ...quoteInvoicingDataForm, [name]: value });
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const updatedForm: QuoteInvoicingDataForm = {
      ...quoteInvoicingDataForm,
      [name]: checked
    };

    if (!checked) {
      updatedForm.standard_weight_value = 0;
      updatedForm.standard_weight_unit_id = null;
      updatedForm.standard_weight_lower_limit = 0;
      updatedForm.standard_weight_lower_limit_unit_id = null;
      // Limpiar errores asociados cuando se desactiva
      onFocusRemove('standard_weight_value');
      onFocusRemove('standard_weight_unit_id');
      onFocusRemove('standard_weight_lower_limit');
      onFocusRemove('standard_weight_lower_limit_unit_id');
    }

    setQuoteInvoicingDataForm(updatedForm);
  };

  const standardWeightEnabled = !!quoteInvoicingDataForm.use_standard_weight;

  return (
    <div className="container-fluid">
      {/* 1. Forma de pago y referencia */}
      <div className="row h4" >  
          <label>Pagos</label>
      </div>
      <div className="row-mb-3">
        <div className="row mb-3">
        <div className="col-md-6">
          <label>Forma de Pago</label>
          <SelectTwo
            name="payment_method_id"
            inputValue={quoteInvoicingDataForm.payment_method_id}
            options={SelectTwoMapperOptions(paymentMethods, 'id', ['name'])}
            hasError={!!fieldHasError('payment_method_id')}
            onChange={v => handleSelect('payment_method_id', v.value)}
            onFocus={() => onFocusRemove('payment_method_id')}
            placeholder="Seleccione"
          />
          <div className="invalid-feedback d-block">
            {fieldErrorMessage('payment_method_id')}
          </div>
        </div>

        <div className="col-md-6">
          <label>Moneda</label>
          <SelectTwo
            name="currency_id"
            inputValue={quoteInvoicingDataForm.currency_id}
            options={SelectTwoMapperOptions(currencies, 'id', ['name'])}
            hasError={!!fieldHasError('currency_id')}
            onChange={v => handleSelect('currency_id', v.value)}
            onFocus={() => onFocusRemove('currency_id')}
            placeholder="Seleccione"
          />
          <div className="invalid-feedback d-block">
            {fieldErrorMessage('currency_id')}
          </div>
        </div>
      </div>
    </div>    

    <div className="row mb-3">
        <div className="col-md-6">
            <label>Condición de Pago</label>
            <div className="row g-1">
              <div className="col-8">
                <SelectTwo
                  name="reference"
                  inputValue={quoteInvoicingDataForm.reference}
                  options={SelectTwoMapperOptions(references)}
                  hasError={!!fieldHasError('reference')}
                  onChange={v => handleSelect('reference', v.value)}
                  onFocus={() => onFocusRemove('reference')}
                  placeholder="Seleccione Referencia"
                />
              </div>
              <div className="col-4">
                <input
                  type="number"
                  name="days_count"
                  className={`form-control ${fieldHasError('days_count') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.days_count}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('days_count')}
                  placeholder="Días"
                />
              </div>
            </div>
            <div className="invalid-feedback d-block">
              {fieldErrorMessage('reference') || fieldErrorMessage('days_count')}
            </div>
          </div>

        <div className="col-md-6">
          <label>Cantidad máxima de pagos</label>
          <SelectTwo
            name="max_payments"
            inputValue={quoteInvoicingDataForm.max_payments ?? QuoteMaxPaymentsEnum.UN_PAGO}
            options={SelectTwoMapperOptions(quoteMaxPaymentsOptions)}
            hasError={!!fieldHasError('max_payments')}
            onChange={v => handleSelect('max_payments', v.value)}
            onFocus={() => onFocusRemove('max_payments')}
            placeholder="Seleccione"
          />
          <div className="form-text">
            Define si los pedidos de esta cotización pueden dividirse en 2 pagos.
          </div>
          <div className="invalid-feedback d-block">
            {fieldErrorMessage('max_payments')}
          </div>
        </div>
      </div>

            <div className="row mb-3">
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="use_standard_weight"
                    name="use_standard_weight"
                    checked={standardWeightEnabled}
                    onChange={handleCheckbox}
                  />
                  <label className="form-check-label" htmlFor="use_standard_weight">
                    Aplicar peso padrón
                  </label>
                </div>
                {standardWeightEnabled && (
                  <div className="alert alert-info mt-2 mb-0" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    <small>
                      <strong>Nota:</strong> El peso padrón no se puede modificar si ya existen Estados de Pago generados desde esta cotización.
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Peso padrón</label>
                <input
                  type="number"
                  name="standard_weight_value"
                  className={`form-control ${standardWeightEnabled && fieldHasError('standard_weight_value') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.standard_weight_value ?? ''}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('standard_weight_value')}
                  placeholder="Valor"
                  disabled={!standardWeightEnabled}
                  min={0}
                />
                {standardWeightEnabled && (
                  <div className="invalid-feedback d-block">
                    {fieldErrorMessage('standard_weight_value')}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">Unidad peso padrón</label>
                <SelectTwo
                  name="standard_weight_unit_id"
                  inputValue={quoteInvoicingDataForm.standard_weight_unit_id}
                  options={SelectTwoMapperOptions(units, 'id', ['name'])}
                  hasError={standardWeightEnabled && !!fieldHasError('standard_weight_unit_id')}
                  onChange={v => handleSelect('standard_weight_unit_id', v?.value ?? null)}
                  onFocus={() => onFocusRemove('standard_weight_unit_id')}
                  placeholder="Seleccione unidad"
                  disabled={!standardWeightEnabled}
                  isClearable
                />
                {standardWeightEnabled && (
                  <div className="invalid-feedback d-block">
                    {fieldErrorMessage('standard_weight_unit_id')}
                  </div>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Límite inferior</label>
                <input
                  type="number"
                  name="standard_weight_lower_limit"
                  className={`form-control ${standardWeightEnabled && fieldHasError('standard_weight_lower_limit') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.standard_weight_lower_limit ?? ''}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('standard_weight_lower_limit')}
                  placeholder="Valor"
                  disabled={!standardWeightEnabled}
                  min={0}
                />
                {standardWeightEnabled && (
                  <div className="invalid-feedback d-block">
                    {fieldErrorMessage('standard_weight_lower_limit')}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label">Unidad límite inferior</label>
                <SelectTwo
                  name="standard_weight_lower_limit_unit_id"
                  inputValue={quoteInvoicingDataForm.standard_weight_lower_limit_unit_id}
                  options={SelectTwoMapperOptions(units, 'id', ['name'])}
                  hasError={standardWeightEnabled && !!fieldHasError('standard_weight_lower_limit_unit_id')}
                  onChange={v => handleSelect('standard_weight_lower_limit_unit_id', v?.value ?? null)}
                  onFocus={() => onFocusRemove('standard_weight_lower_limit_unit_id')}
                  placeholder="Seleccione unidad"
                  disabled={!standardWeightEnabled}
                  isClearable
                />
                {standardWeightEnabled && (
                  <div className="invalid-feedback d-block">
                    {fieldErrorMessage('standard_weight_lower_limit_unit_id')}
                  </div>
                )}
              </div>
            </div>

      {/*Tipo de cambio preliminar y final */}
      {!isCLP &&
      <div>
        <div className="row h4" >  
          <label>Tipo de Cambio</label>
        </div> 
        <div className="row mb-3">  
          <div className="col-md-6">
            <label>Fuente</label>
            <SelectTwo
              name="exchange_rate_date_source"
              inputValue={quoteInvoicingDataForm.exchange_rate_date_source}
              options={SelectTwoMapperOptions(exchangeRateSource)}
              hasError={!!fieldHasError('exchange_rate_date_source')}
              onChange={v => handleSelect('exchange_rate_date_source', v.value)}
              onFocus={() => onFocusRemove('exchange_rate_date_source')}
              placeholder="Seleccione"
            />
            <div className="invalid-feedback d-block">
                {fieldErrorMessage('exchange_rate_date_source')}
            </div>
          </div>
        </div>

        {/* Preliminar */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label>Tipo de Cambio Prov</label>
            <SelectTwo
              name="preliminary_exchange_rate_date_reference"
              inputValue={quoteInvoicingDataForm.preliminary_exchange_rate_date_reference}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              hasError={!!fieldHasError('preliminary_exchange_rate_date_reference')}
              onChange={v => handleSelect('preliminary_exchange_rate_date_reference', v.value)}
              onFocus={() => onFocusRemove('preliminary_exchange_rate_date_reference')}
              placeholder="Seleccione"
            />
            <div className="invalid-feedback d-block">
              {fieldErrorMessage('preliminary_exchange_rate_date_reference')}
            </div>
          </div>

          {showPrelimFixed ? (
          <>
          <div className="col-md-6">
            <label>Fecha Fija (Prov)</label>
            <input
              type="date"
              name="preliminary_fixed_date"
              className={`form-control ${fieldHasError('preliminary_fixed_date') ? 'is-invalid' : ''}`}
              value={quoteInvoicingDataForm.preliminary_fixed_date || ''}
              onChange={handleChange}
              onFocus={() => onFocusRemove('preliminary_fixed_date')}
            />
            <div className="invalid-feedback d-block">
              {fieldErrorMessage('preliminary_fixed_date')}
            </div>
          </div>
          </>
          ) : (
            <div className="col-md-6 row">
              <div className="col-4">
                <label className="form-label">Días (Prov)</label>
                <input
                  type="number"
                  name="preliminary_days"
                  className={`form-control ${fieldHasError('preliminary_days') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.preliminary_days}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('preliminary_days')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('preliminary_days')}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label">Meses (Prov)</label>
                <input
                  type="number"
                  name="preliminary_months"
                  className={`form-control ${fieldHasError('preliminary_months') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.preliminary_months}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('preliminary_months')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('preliminary_months')}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label">Año (Prov)</label>
                <input
                  type="number"
                  name="preliminary_year"
                  className={`form-control ${fieldHasError('preliminary_year') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.preliminary_year}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('preliminary_year')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('preliminary_year')}
                </div>
              </div>
            </div>
          )}
        </div> 

        {/* Final */}
        <div className="row mb-3">  
          <div className="col-md-6">
            <label>Tipo de Cambio Final</label>
            <SelectTwo
              name="final_exchange_rate_date_reference"
              inputValue={quoteInvoicingDataForm.final_exchange_rate_date_reference}
              options={SelectTwoMapperOptions(exchangeRateDate)}
              hasError={!!fieldHasError('final_exchange_rate_date_reference')}
              onChange={v => handleSelect('final_exchange_rate_date_reference', v.value)}
              onFocus={() => onFocusRemove('final_exchange_rate_date_reference')}
              placeholder="Seleccione"
            />
            <div className="invalid-feedback d-block">
              {fieldErrorMessage('final_exchange_rate_date_reference')}
            </div>
          </div>


          {showFinalFixed ? (
          <>
            <div className="col-md-6">
              <label>Fecha Fija (Final)</label>
              <input
                type="date"
                name="final_fixed_date"
                className={`form-control ${fieldHasError('final_fixed_date') ? 'is-invalid' : ''}`}
                value={quoteInvoicingDataForm.final_fixed_date || ''}
                onChange={handleChange}
                onFocus={() => onFocusRemove('final_fixed_date')}
              />
              <div className="invalid-feedback d-block">
                {fieldErrorMessage('final_fixed_date')}
              </div>
            </div>
          </>
          ) : (
            <div className="col-md-6 row">
              <div className="col-4">
                <label className="form-label">Días (Final)</label>
                <input
                  type="number"
                  name="final_days"
                  className={`form-control ${fieldHasError('final_days') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.final_days}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('final_days')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('final_days')}
                </div>
              </div>
              <div className="col-4">
                <label className="form-label">Meses (Final)</label>
                <input
                  type="number"
                  name="final_months"
                  className={`form-control ${fieldHasError('final_months') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.final_months}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('final_months')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('final_months')}
                </div>
              </div> 
              <div className="col-4">
                <label className="form-label">Año (Final)</label>
                <input
                  type="number"
                  name="final_year"
                  className={`form-control ${fieldHasError('final_year') ? 'is-invalid' : ''}`}
                  value={quoteInvoicingDataForm.final_year}
                  onChange={handleChange}
                  onFocus={() => onFocusRemove('final_year')}
                />
                <div className="invalid-feedback d-block">
                  {fieldErrorMessage('final_year')}
                </div>
              </div>
            </div>
          )
          }
        </div>
      </div>  
    }


    </div>
  );
};

export default QuoteInvoicingForm;
